import { useIntegrations } from "@/api/integration";
import { IntegrationIcon } from "@/components/ui/select-new";
import { pickPrimaryProvider } from "@/lib/customer-data/pick-source";
import { cn } from "@/lib/utils";
import { useActiveGym } from "@/ui/hooks/active-gym";

/**
 * Icon for system-generated assets (system segments, flows, message
 * templates). System assets don't store which integration created them, so we
 * resolve the icon from the gym's primary installed integration instead of
 * hardcoding a single provider.
 */
export function SystemIntegrationIcon({ className }: { className?: string }) {
  const { gym } = useActiveGym();
  const { data: integrations } = useIntegrations({ gym: gym?.id }, { continue: () => !!gym?.id });
  const provider = pickPrimaryProvider(integrations);

  return <IntegrationIcon integration={provider} className={className} />;
}

/**
 * Framed integration icon that marks an asset as a system-generated default.
 * The bordered frame signals "Helios set this up for you" while keeping the
 * gym's integration logo as the visual cue. Used as a leading marker on system
 * segments/flows/templates.
 */
export function SystemBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex size-[1.375rem] shrink-0 items-center justify-center rounded-md border bg-muted/40",
        className,
      )}
    >
      <SystemIntegrationIcon className="size-4" />
    </span>
  );
}
